/* istanbul ignore file */
import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import * as blueprint from '@adc/blueprint'
import * as graph from '@adc/sdk'
import classnames from 'classnames/bind'
import { Placeholder } from 'semantic-ui-react'
import Button from '../../elementals/button'
import AddressInfo from '../addressInfo'
import cls from './cardPartsStyles.scss'
import {
  getAddressLine,
  getOpeningBidValue,
  valuationEstimate,
  getDisplayedValue,
  MISSING_VALUE_LABEL,
  shouldDisplayReserveMet,
  LISTING_CARD_PHOTO_WIDTH,
} from './cardHelpers'
import { enums } from '@adc/sdk'
import { ListingBadgesTags } from '../badges'
import PropertySaveIndicator from '../../property/propertySaveIndicator'
import ListingIndicator from '../listingIndicator'
import { useBackgroundImageWithFallback } from '../../../hooks/useBackgroundImageWithFallback'
import { formatListingImgUrl } from '../utils/formatters'
import SmartSaleLogo from '../../../images/smartsale-logo.png'

const cx = classnames.bind(cls)

export const linkPropType = PropTypes.oneOfType([
  PropTypes.shape({
    onClick: PropTypes.function,
    onMouseLeave: PropTypes.function,
    href: PropTypes.string,
  }),
  () => null,
])

export const HOVER_ACTIONS = {
  SHOW: 'SHOW',
  HIDE: 'HIDE',
  TOGGLE: 'TOGGLE',
}

export const PropertyInfo = ({
  listing,
  elmId,
  isHoverMode = false,
  className,
  isCancelled = false,
}) => {
  const {
    total_bedrooms: beds,
    total_bathrooms: baths,
    square_footage: sqft,
    lot_size: lotSize,
    year_built: yearBuilt,
  } = listing.primary_property.summary

  return (
    <div className={cx('property-info', className)}>
      {!isCancelled && (
        <div className={cx('primary-info')}>
          {!!beds && (
            <div
              className={cx({
                separator: baths || sqft,
              })}
            >
              <span
                className={cx('value')}
                data-elm-id={`card_auction_beds_${elmId}`}
              >
                {beds}
              </span>{' '}
              bd
            </div>
          )}
          {!!beds && !!baths && (
            <div className={cx({ separator: sqft })}>
              <span
                className={cx('value')}
                data-elm-id={`card_auction_baths_${elmId}`}
              >
                {baths}
              </span>{' '}
              ba
            </div>
          )}
          {!!sqft && (
            <div className={cx('truncate')}>
              <span
                className={cx('value')}
                data-elm-id={`card_auction_sqft_${elmId}`}
              >
                {Number(sqft).toLocaleString()}
              </span>{' '}
              sq. ft.
            </div>
          )}
        </div>
      )}
      {isHoverMode && (lotSize || yearBuilt) && (
        <div className={cx('secondary-info')}>
          {lotSize && (
            <div className={cx({ separator: yearBuilt })}>
              Lot Size:{' '}
              <span className={cx('value')}>{`${lotSize} Acres`}</span>
            </div>
          )}
          {yearBuilt && (
            <div>
              Year Built: <span className={cx('value')}>{yearBuilt}</span>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

PropertyInfo.propTypes = {
  listing: PropTypes.object,
  elmId: PropTypes.string,
  isHoverMode: PropTypes.bool,
  className: PropTypes.string,
  isCancelled: PropTypes.bool,
}

export const ListingCardIndicator = ({ listing, className, ...restProps }) => {
  return (
    <ListingIndicator
      {...restProps}
      listing={listing}
      hideAuctionDate={
        listing.online_auction_segment?.segment_type ===
        enums.OnlineAuctionSegmentType.SealedBid
      }
      className={cx('asset-info', className)}
    />
  )
}

ListingCardIndicator.propTypes = {
  className: PropTypes.string,
  listing: PropTypes.object,
}

const Prices = ({ prices, elmId }) =>
  prices && prices.length ? (
    <div className={cx('prices')} data-elm-id={`card_prices_${elmId}`}>
      {prices.slice(0, 2).map((price, index) =>
        price.amount ? (
          <div
            className={cx('price')}
            key={index}
            data-elm-id={`card_price_${index}`}
          >
            <label data-elm-id='card_price_label'>
              {price.label}:<span className={cx('dots')}>{'.'.repeat(50)}</span>
            </label>
            <div className={cx('amount')} data-elm-id='card_price_amount'>
              {price.amount}
            </div>
          </div>
        ) : null
      )}
    </div>
  ) : null

Prices.propTypes = {
  prices: PropTypes.array,
  elmId: PropTypes.string,
}

const FooterLower = ({ listing, elmId, linkProps }) => {
  return (
    <div data-elm-id={`footer-lower_${elmId}`} className={cx('footer-lower')}>
      <div className={cx('opening-bid')}>
        {listing.auction.starting_bid && (
          <div>
            Opening Bid:
            <p className={cx('amount')}>{getOpeningBidValue(listing)}</p>
          </div>
        )}
      </div>
      <div>
        <Button
          size='small'
          role='primary'
          onClick={linkProps.onClick}
          link={linkProps.href}
        >
          View Now
        </Button>
      </div>
    </div>
  )
}

FooterLower.propTypes = {
  listing: PropTypes.object,
  elmId: PropTypes.string,
  linkProps: linkPropType,
}

// ------- NEW PROPERTY CARD PARTS ------

export const listingAddressPropTypes = PropTypes.shape({
  street_description: PropTypes.string,
  municipality: PropTypes.string,
  country_primary_subdivision: PropTypes.string,
  country_secondary_subdivision: PropTypes.string,
  postal_code: PropTypes.string,
})

export const listingPropType = PropTypes.shape({
  listing_id: PropTypes.string.isRequired,
  primary_photo: PropTypes.string,
  listing_photos_count: PropTypes.number,
  listing_page_path: PropTypes.string.isRequired,
  reserve_price: PropTypes.number,
  is_hot: PropTypes.bool,
  listing_status_label: PropTypes.string.isRequired,
  listing_configuration: PropTypes.shape({
    product_type: PropTypes.string.isRequired,
    broker_commission: PropTypes.number,
    financing_available: PropTypes.bool,
    buyer_premium_available: PropTypes.bool,
    interior_access_allowed: PropTypes.bool,
    occupancy_status: PropTypes.string,
    is_reserve_displayed: PropTypes.bool,
    mlh_seller_code: PropTypes.string,
    asset_type: PropTypes.string,
    is_direct_offer_enabled: PropTypes.bool,
    is_third_party_online: PropTypes.bool,
  }),
  listing_summary: PropTypes.shape({
    is_remote_bid_enabled: PropTypes.bool,
    is_remote_before_and_during_auction_enabled: PropTypes.bool,
    show_opening_bid: PropTypes.bool,
  }),
  event: PropTypes.shape({
    event_code: PropTypes.string,
  }),
  seller_property: listingAddressPropTypes,
  primary_property: PropTypes.shape({
    property_id: PropTypes.string,
    summary: PropTypes.shape({
      street_address: PropTypes.string,
      total_bedrooms: PropTypes.number,
      total_bathrooms: PropTypes.number,
      square_footage: PropTypes.number,
      lot_size: PropTypes.number,
      year_built: PropTypes.number,
      valuation: PropTypes.number,
      structure_type_code: PropTypes.string,
    }),
    is_currently_saved: PropTypes.bool,
    is_newly_listed: PropTypes.bool,
    current_user_tracking_state: PropTypes.shape({
      is_seen: PropTypes.bool,
      is_updated: PropTypes.bool,
    }),
  }),
  auction: PropTypes.shape({
    start_date: PropTypes.string,
    end_date: PropTypes.string,
    starting_bid: PropTypes.number,
    is_online: PropTypes.bool,
    bid_instruction: PropTypes.shape({
      nos_amount: PropTypes.number,
    }),
  }),
  parties: PropTypes.arrayOf(
    PropTypes.shape({
      party_role: PropTypes.string,
      party_name: PropTypes.string,
    })
  ),
  external_information: PropTypes.shape({
    collateral: PropTypes.shape({
      summary: PropTypes.arrayOf(
        PropTypes.shape({
          estimate: PropTypes.number,
          high: PropTypes.number,
          type: PropTypes.string,
        })
      ),
    }),
  }),
})

export const PropertyType = ({ listing, featureFlags, isHorizontalView }) => {
  const propertyTypeInfo = blueprint.listing.utils.getPropertyTypeInfo({
    listing,
    featureFlags,
  })
  if (!propertyTypeInfo) {
    return null
  }
  const { combinedTextArray, offerType, structureInfoCombined } =
    propertyTypeInfo

  return (
    <div
      className={cx('property-type', { 'horizontal-view': isHorizontalView })}
      title={structureInfoCombined}
    >
      {offerType && (
        <span>
          <span>
            <b>{offerType}</b>
          </span>
        </span>
      )}
      {combinedTextArray.map((text, index) => (
        <React.Fragment key={index}>
          {index > 0 || offerType ? ' | ' : ''}
          {text}
        </React.Fragment>
      ))}
    </div>
  )
}

PropertyType.propTypes = {
  listing: listingPropType,
  featureFlags: PropTypes.object,
  isHorizontalView: PropTypes.bool,
}

export const LeftSidePropertyValue = ({ listing, isPopup = false }) => {
  const displayedValues = getDisplayedValue(listing)
  const displayReserveMet = shouldDisplayReserveMet(listing)

  return (
    <div className={cx({ 'popup-property-value': isPopup })}>
      <div className={cx('labels-container')}>
        <span className={cx('amount-label')}>{displayedValues.label}</span>
      </div>
      <div className={cx('labels-container')}>
        {displayedValues.amount ? (
          <div
            title={displayedValues.amount}
            className={cx('amount', {
              muted: displayedValues.amount === MISSING_VALUE_LABEL,
            })}
          >
            {displayedValues.amount}
          </div>
        ) : null}
        {displayReserveMet && !isPopup && (
          <div title='Reserve Met' className={cx('reserve-met')}>
            Reserve Met
          </div>
        )}
      </div>
    </div>
  )
}

LeftSidePropertyValue.propTypes = {
  listing: listingPropType,
  isPopup: PropTypes.bool,
}

export const PropertyValue = ({
  listing,
  elmId,
  isHorizontalView = false,
  featureFlags = {},
}) => {
  const shouldDisplaySmartSaleLogo =
    featureFlags['fe.resi.enable-private-seller'] &&
    blueprint.listing.utils.isSmartSale(listing) &&
    !blueprint.listing.utils.isSparkOffer(listing)
  const valuationContent = (
    <div>
      <span className={cx('amount-label')}>Est. Resale Value</span>
      {listing.primary_property?.summary?.valuation ? (
        <p className={cx('amount')}>{valuationEstimate(listing)}</p>
      ) : (
        <p className={cx('amount', 'muted')}>{MISSING_VALUE_LABEL}</p>
      )}
    </div>
  )

  return (
    <div
      className={cx('property-value-container', {
        'horizontal-view': isHorizontalView,
      })}
    >
      <div
        className={cx('opening-bid')}
        data-elm-id={`property_value_left_column_${elmId}`}
      >
        <LeftSidePropertyValue listing={listing} />
      </div>
      <div
        className={cx('reserve-value')}
        data-elm-id={`property_value_right_column_${elmId}`}
      >
        {shouldDisplaySmartSaleLogo ? (
          <div className={cx('smart-sale-logo-container')}>
            <img src={SmartSaleLogo} />
          </div>
        ) : (
          valuationContent
        )}
      </div>
    </div>
  )
}

PropertyValue.propTypes = {
  listing: listingPropType,
  elmId: PropTypes.string,
  featureFlags: PropTypes.object,
  isHorizontalView: PropTypes.bool,
}

export const AddressInfoLine = ({
  address = [],
  elmId,
  className,
  isCancelled,
}) => {
  return (
    <div>
      <div
        data-elm-id={`address_line_${elmId}`}
        className={cx('property-address-line', className)}
      >
        <AddressInfo
          address={address}
          elmId={elmId}
          isCancelled={isCancelled}
        />
      </div>
    </div>
  )
}

AddressInfoLine.propTypes = {
  address: PropTypes.arrayOf(PropTypes.string),
  elmId: PropTypes.string,
  className: PropTypes.string,
  isCancelled: PropTypes.bool,
}

export const CardImage = ({
  listing,
  elmId,
  showSave,
  showImageCount = true,
  onSaveToggle,
  stopPropagationClick,
  loading,
  maxBadges,
  isCancelled,
  isHorizontalView = false,
  className = '',
  featureFlags = {},
  verifiedImage,
  updateVerifiedImage,
  viewingFrom = graph.enums.CurrentTargetAudience.Buyer,
  badgeClassName = '',
}) => {
  const altTextImage = getAddressLine(listing.seller_property)

  const auxPhoto = useBackgroundImageWithFallback(
    listing.primary_photo,
    featureFlags['fe.resi.enabled-photo-enhancement'],
    verifiedImage,
    // We pass a callback function to the hook to handle the possibility of this component
    // unmounting before the auxPhoto has finished verifying (possible due to the SRP Dynamic
    // card list which ofter refreshes the entire list while scrolling)
    () => updateVerifiedImage && updateVerifiedImage(listing.listing_id, null)
  )

  let primaryPhoto
  if (verifiedImage) {
    /* WHY: the verifiedImage prop is here to allow the parent app to "cache" already
      verified photos. This is useful due to the fact that apps like resi-search reload 
      the entire Listing Card list for even small changes, which means the CardImage 
      component is mounted again for every change. This combined with the fact that  
      useBackgroundImageWithFallback works asynchronously, it can potentially cause a card
      list to flicker while loading the valid URL on any state change on SRP, even just 
      hovering over a card.  If verifiedImage and updateVerified image are not set, this can
      be safely ignored */
    primaryPhoto = verifiedImage
  } else {
    primaryPhoto = auxPhoto
  }

  useEffect(() => {
    if (updateVerifiedImage && auxPhoto !== verifiedImage) {
      updateVerifiedImage(listing.listing_id, auxPhoto)
    }
  }, [auxPhoto, verifiedImage, updateVerifiedImage])

  const listingPhotoUrl = formatListingImgUrl(primaryPhoto, {
    width: LISTING_CARD_PHOTO_WIDTH,
  })

  return loading ? (
    <div
      className={cx(
        'property-card-image-container',
        {
          'horizontal-view': isHorizontalView,
        },
        className
      )}
    >
      <Placeholder fluid style={{ height: '100%' }}>
        <Placeholder.Image />
      </Placeholder>
    </div>
  ) : (
    <div
      className={cx(
        'property-card-image-container',
        {
          'horizontal-view': isHorizontalView,
        },
        className
      )}
    >
      <span
        className={cx('property-card-image-background')}
        style={{
          backgroundImage: `url(${listingPhotoUrl})`,
        }}
        role='img'
        aria-label={altTextImage}
      />
      <ListingBadgesTags
        listing={listing}
        elmId={elmId}
        isHorizontalView={isHorizontalView}
        maxBadges={maxBadges}
        viewingFrom={viewingFrom}
        className={badgeClassName}
      />
      {showSave && (
        <span className={cx('save-button')} onClick={stopPropagationClick}>
          <PropertySaveIndicator
            elmId={elmId}
            property={listing.primary_property}
            onSaveToggle={onSaveToggle}
            isCancelled={isCancelled}
          />
        </span>
      )}
      {showImageCount && listing.listing_photos_count > 1 ? (
        <div
          className={cx('photo-count-badge')}
        >{`${listing.listing_photos_count} images`}</div>
      ) : null}
    </div>
  )
}

CardImage.propTypes = {
  listing: listingPropType,
  elmId: PropTypes.string,
  showSave: PropTypes.bool,
  showImageCount: PropTypes.bool,
  onSaveToggle: PropTypes.func,
  stopPropagationClick: PropTypes.func,
  isHorizontalView: PropTypes.bool,
  loading: PropTypes.bool,
  maxBadges: PropTypes.number,
  isCancelled: PropTypes.bool,
  className: PropTypes.string,
  featureFlags: PropTypes.object,
  verifiedImage: PropTypes.string,
  updateVerifiedImage: PropTypes.func,
  viewingFrom: PropTypes.oneOf(
    Object.values(graph.enums.CurrentTargetAudience)
  ),
  badgeClassName: PropTypes.string,
}
